import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  IconEye,
  IconPackage,
  IconStar,
  IconWorld,
  IconListCheck,
  IconTrendingUp,
  IconHeartRateMonitor,
  IconAlertTriangle,
  IconApps,
  IconExternalLink,
  IconChevronDown,
} from '@tabler/icons-react';
import { Collapse, Tooltip, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { clientRoutes } from '@/ApiPaths';
import { Listing } from '@/types/Listing.model';
import {
  dailyIncrements,
  buildDisplaySeries,
  formatPercent,
  percentChange,
  readHistory,
  shortDayLabel,
  upsertToday,
  writeHistory,
  type DayPoint,
} from '@/utils/dashboardInsightsHistory';
import classes from './DashboardInsights.module.css';

const EXPANDED_STORAGE_KEY = 'robotop.dashboardInsights.expanded';

function readExpandedPreference(userId?: string): boolean {
  try {
    const raw = localStorage.getItem(
      userId ? `${EXPANDED_STORAGE_KEY}.${userId}` : EXPANDED_STORAGE_KEY
    );
    if (raw == null) return true;
    return raw === '1';
  } catch {
    return true;
  }
}

function writeExpandedPreference(expanded: boolean, userId?: string) {
  try {
    localStorage.setItem(
      userId ? `${EXPANDED_STORAGE_KEY}.${userId}` : EXPANDED_STORAGE_KEY,
      expanded ? '1' : '0'
    );
  } catch {
    // ignore quota / private mode
  }
}

export type DashboardInsightListing = {
  isConfirmed?: boolean;
  status?: string;
  appConfirmation?: string;
  productConfirmation?: string;
};

export type DashboardInsightProduct = {
  _id?: string;
  productName?: string;
  views?: number;
  starCount?: number;
  isPublic?: boolean;
  listings?: DashboardInsightListing[];
};

/** Match ListingStatusBadge / server virtual — lean API payloads omit isConfirmed. */
function isListingConfirmed(listing: DashboardInsightListing): boolean {
  if (listing.isConfirmed === true) return true;
  if (listing.status === Listing.Status.LISTED) return true;
  return (
    listing.appConfirmation === Listing.Confirmation.CONFIRMED &&
    listing.productConfirmation === Listing.Confirmation.CONFIRMED
  );
}

function countConfirmedListings(listings?: DashboardInsightListing[]): number {
  return listings?.filter(isListingConfirmed).length ?? 0;
}

type DashboardInsightsProps = {
  products: DashboardInsightProduct[];
  accentColor?: string;
  userId?: string;
};

function formatDelta(delta: number | null): string | null {
  if (delta == null || delta === 0) return null;
  return delta > 0 ? `+${delta}` : `${delta}`;
}

function TrendLineChart({
  series,
  metric,
  accentColor,
  locale,
  emptyLabel,
}: {
  series: DayPoint[];
  metric: 'views' | 'stars';
  accentColor: string;
  locale: string;
  emptyLabel: string;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const width = 320;
  const height = 120;
  const padX = 8;
  const padY = 12;

  if (series.length < 2) {
    return <p className={classes.emptyChart}>{emptyLabel}</p>;
  }

  const values = series.map((d) => d[metric]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);

  const points = series.map((d, i) => {
    const x = padX + (i / (series.length - 1)) * (width - padX * 2);
    const y = height - padY - ((d[metric] - min) / span) * (height - padY * 2);
    return { x, y, date: d.date, value: d[metric] };
  });

  const line = points.map((p) => `${p.x},${p.y}`).join(' ');
  const area = `${points[0].x},${height - padY} ${line} ${points[points.length - 1].x},${height - padY}`;
  const labelIndexes = [0, Math.floor((series.length - 1) / 2), series.length - 1];
  const active = hoverIndex != null ? points[hoverIndex] : null;

  return (
    <div
      className={classes.trendChart}
      onMouseLeave={() => setHoverIndex(null)}
    >
      <div className={classes.trendPlot}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className={classes.trendSvg}
          role="img"
          aria-label={metric}
        >
          <polygon points={area} fill={accentColor} opacity={0.12} />
          <polyline
            points={line}
            fill="none"
            stroke={accentColor}
            strokeWidth={2.25}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {active ? (
            <line
              x1={active.x}
              y1={padY / 2}
              x2={active.x}
              y2={height - padY / 2}
              stroke={accentColor}
              strokeOpacity={0.25}
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          ) : null}
          {points.map((p, i) => {
            const isActive = hoverIndex === i;
            return (
              <g key={p.date}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={isActive ? 5 : 2.75}
                  fill={accentColor}
                  className={classes.trendDot}
                />
                {/* Larger invisible hit target */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={14}
                  fill="transparent"
                  className={classes.trendHit}
                  onMouseEnter={() => setHoverIndex(i)}
                  onFocus={() => setHoverIndex(i)}
                />
              </g>
            );
          })}
        </svg>

        {active && hoverIndex != null ? (
          <div
            className={classes.trendTooltip}
            style={{
              left: `${(active.x / width) * 100}%`,
              top: `${(active.y / height) * 100}%`,
              ['--tooltip-accent' as string]: accentColor,
            }}
            role="tooltip"
          >
            <span className={classes.trendTooltipValue}>{active.value.toLocaleString()}</span>
            <span className={classes.trendTooltipMeta}>
              {shortDayLabel(active.date, locale)}
            </span>
          </div>
        ) : null}
      </div>
      <div className={classes.trendLabels}>
        {labelIndexes.map((i) => (
          <span key={`${series[i].date}-${i}`} className={classes.trendLabel}>
            {shortDayLabel(series[i].date, locale)}
          </span>
        ))}
      </div>
    </div>
  );
}

function DailyGainBars({
  series,
  metric,
  accentColor,
  locale,
  emptyLabel,
}: {
  series: DayPoint[];
  metric: 'views' | 'stars';
  accentColor: string;
  locale: string;
  emptyLabel: string;
}) {
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const gains = dailyIncrements(series, metric).slice(-14);
  const max = Math.max(...gains.map((g) => g.value), 1);

  if (series.length < 2) {
    return <p className={classes.emptyChart}>{emptyLabel}</p>;
  }

  return (
    <div className={classes.gainChart} onMouseLeave={() => setHoverDate(null)}>
      <div className={classes.gainBars}>
        {gains.map((g) => {
          const isActive = hoverDate === g.date;
          return (
            <div
              key={g.date}
              className={`${classes.gainCol}${isActive ? ` ${classes.gainColActive}` : ''}`}
              onMouseEnter={() => setHoverDate(g.date)}
            >
              {isActive ? (
                <div
                  className={classes.gainTooltip}
                  style={{ ['--tooltip-accent' as string]: accentColor }}
                  role="tooltip"
                >
                  <span className={classes.gainTooltipValue}>+{g.value.toLocaleString()}</span>
                  <span className={classes.gainTooltipMeta}>
                    {shortDayLabel(g.date, locale)}
                  </span>
                </div>
              ) : null}
              <div className={classes.gainTrack}>
                <div
                  className={classes.gainFill}
                  style={{
                    height: `${Math.max(g.value > 0 ? 8 : 2, (g.value / max) * 100)}%`,
                    background: accentColor,
                    opacity: g.value > 0 ? (isActive ? 1 : 0.9) : 0.25,
                    transform: isActive ? 'scaleX(1.15)' : undefined,
                  }}
                />
              </div>
              <span className={classes.gainDay}>{shortDayLabel(g.date, locale).split(' ')[0]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopBarList({
  items,
  accentColor,
  emptyLabel,
  fillOpacity = 1,
  expanded,
}: {
  items: { id: string; name: string; value: number; display?: string }[];
  accentColor: string;
  emptyLabel: string;
  fillOpacity?: number;
  /** When false and list has multiple items, only rank #1 is shown. */
  expanded: boolean;
}) {
  const { t } = useTranslation();
  const max = Math.max(...items.map((i) => i.value), 1);
  const openLabel = t('dashboard.insights.openProduct');
  const visibleItems =
    expanded || items.length <= 1 ? items : items.slice(0, 1);

  if (items.length === 0) {
    return <p className={classes.emptyChart}>{emptyLabel}</p>;
  }

  return (
    <ul className={classes.barList}>
      {visibleItems.map((item, index) => (
        <li
          key={item.id}
          className={classes.barRow}
          style={{ animationDelay: `${index * 45}ms` }}
        >
          <div className={classes.barMeta}>
            <span className={classes.barRank}>{index + 1}</span>
            <span className={classes.barNameWrap}>
              <span className={classes.barName} title={item.name}>
                {item.name}
              </span>
              <Tooltip label={openLabel} withArrow position="top" openDelay={200}>
                <Link
                  to={clientRoutes.product.replace(':id', item.id)}
                  className={classes.barOpen}
                  aria-label={openLabel}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconExternalLink size={9} stroke={2} />
                </Link>
              </Tooltip>
            </span>
            <span className={classes.barValue}>{item.display ?? item.value}</span>
          </div>
          <div className={classes.barTrack} aria-hidden>
            <div
              className={classes.barFill}
              style={
                {
                  width: `${Math.max(6, (item.value / max) * 100)}%`,
                  '--bar-accent': accentColor,
                  opacity: fillOpacity,
                } as CSSProperties
              }
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ExpandableTopListCard({
  title,
  icon,
  hint,
  items,
  accentColor,
  emptyLabel,
  fillOpacity,
}: {
  title: string;
  icon: ReactNode;
  hint?: string;
  items: { id: string; name: string; value: number; display?: string }[];
  accentColor: string;
  emptyLabel: string;
  fillOpacity?: number;
}) {
  const { t } = useTranslation();
  const [listExpanded, setListExpanded] = useState(false);
  const canExpand = items.length > 1;
  const expandLabel = t('dashboard.insights.expandTopList', { count: items.length });
  const collapseLabel = t('dashboard.insights.collapseTopList');
  const toggleLabel = listExpanded ? collapseLabel : expandLabel;

  return (
    <div className={classes.chartCard}>
      <div className={`${classes.chartHeaderRow} ${classes.topListHeader}`}>
        <div className={classes.chartHeader}>
          {icon}
          <h3 className={classes.chartTitle}>{title}</h3>
        </div>
        {canExpand ? (
          <Tooltip label={toggleLabel} withArrow position="left" openDelay={200}>
            <UnstyledButton
              type="button"
              className={`${classes.barExpandBtn} ${listExpanded ? classes.barExpandBtnOpen : ''}`}
              onClick={() => setListExpanded((v) => !v)}
              aria-expanded={listExpanded}
              aria-label={toggleLabel}
            >
              <IconChevronDown size={13} stroke={1.75} />
            </UnstyledButton>
          </Tooltip>
        ) : null}
      </div>
      {hint ? <p className={classes.listHint}>{hint}</p> : null}
      <TopBarList
        items={items}
        accentColor={accentColor}
        emptyLabel={emptyLabel}
        fillOpacity={fillOpacity}
        expanded={listExpanded || !canExpand}
      />
    </div>
  );
}

/**
 * Portfolio overview: aggregates, top lists, and day-by-day growth charts.
 * Daily history is stored locally until a server analytics API exists.
 */
export function DashboardInsights({
  products,
  accentColor = DEFAULT_QR_CODE_COLOR,
  userId,
}: DashboardInsightsProps) {
  const { t, i18n } = useTranslation();
  const [history, setHistory] = useState<DayPoint[]>([]);
  const [expanded, setExpanded] = useState(() => readExpandedPreference(userId));

  useEffect(() => {
    setExpanded(readExpandedPreference(userId));
  }, [userId]);

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      writeExpandedPreference(next, userId);
      return next;
    });
  };

  const stats = useMemo(() => {
    let totalViews = 0;
    let totalStars = 0;
    let publicCount = 0;
    let listedCount = 0;

    for (const product of products) {
      totalViews += product.views ?? 0;
      totalStars += product.starCount ?? 0;
      if (product.isPublic) publicCount += 1;
      if (countConfirmedListings(product.listings) > 0) listedCount += 1;
    }

    const byViews = [...products]
      .filter((p) => p._id && (p.views ?? 0) > 0)
      .sort((a, b) => (b.views ?? 0) - (a.views ?? 0))
      .slice(0, 5)
      .map((p) => ({
        id: p._id as string,
        name: p.productName?.trim() || t('dashboard.insights.untitled'),
        value: p.views ?? 0,
      }));

    const byStars = [...products]
      .filter((p) => p._id && (p.starCount ?? 0) > 0)
      .sort((a, b) => (b.starCount ?? 0) - (a.starCount ?? 0))
      .slice(0, 5)
      .map((p) => ({
        id: p._id as string,
        name: p.productName?.trim() || t('dashboard.insights.untitled'),
        value: p.starCount ?? 0,
      }));

    // Stars per 100 views — only products with enough traffic to be meaningful
    const byEngagement = [...products]
      .filter((p) => p._id && (p.views ?? 0) >= 5)
      .map((p) => {
        const views = p.views ?? 0;
        const stars = p.starCount ?? 0;
        const rate = (stars / views) * 100;
        return {
          id: p._id as string,
          name: p.productName?.trim() || t('dashboard.insights.untitled'),
          value: rate,
          display: `${rate.toFixed(1)}%`,
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Public products with the least views — share / improve these
    const needsAttention = [...products]
      .filter((p) => p._id && p.isPublic === true)
      .sort((a, b) => (a.views ?? 0) - (b.views ?? 0))
      .slice(0, 5)
      .map((p) => ({
        id: p._id as string,
        name: p.productName?.trim() || t('dashboard.insights.untitled'),
        value: p.views ?? 0,
        display: String(p.views ?? 0),
      }));

    const byListings = [...products]
      .map((p) => {
        const confirmed = countConfirmedListings(p.listings);
        return {
          id: p._id as string,
          name: p.productName?.trim() || t('dashboard.insights.untitled'),
          value: confirmed,
        };
      })
      .filter((p) => p.id && p.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Private products that already have traffic — candidates to go public
    const privateWithViews = [...products]
      .filter((p) => p._id && p.isPublic === false && (p.views ?? 0) > 0)
      .sort((a, b) => (b.views ?? 0) - (a.views ?? 0))
      .slice(0, 5)
      .map((p) => ({
        id: p._id as string,
        name: p.productName?.trim() || t('dashboard.insights.untitled'),
        value: p.views ?? 0,
      }));

    return {
      totalProducts: products.length,
      totalViews,
      totalStars,
      publicCount,
      listedCount,
      byViews,
      byStars,
      byEngagement,
      needsAttention,
      byListings,
      privateWithViews,
    };
  }, [products, t]);

  useEffect(() => {
    if (!userId || products.length === 0) return;
    const prev = readHistory(userId);
    const next = upsertToday(prev, {
      views: stats.totalViews,
      stars: stats.totalStars,
    });
    writeHistory(userId, next);
    setHistory(next);
  }, [userId, products.length, stats.totalViews, stats.totalStars]);

  const filled = useMemo(() => buildDisplaySeries(history, 7), [history]);

  const growth = useMemo(() => {
    if (filled.length < 2) {
      return {
        viewsDay: null as number | null,
        starsDay: null as number | null,
        viewsDayPct: null as number | null,
        starsDayPct: null as number | null,
        viewsPeriodPct: null as number | null,
        starsPeriodPct: null as number | null,
        viewsDayAbs: null as number | null,
        starsDayAbs: null as number | null,
        periodDays: 0,
        hasRealHistory: false,
      };
    }
    const prev = filled[filled.length - 2];
    const curr = filled[filled.length - 1];
    const first = filled[0];
    const viewsDayAbs = curr.views - prev.views;
    const starsDayAbs = curr.stars - prev.stars;
    // At least two real dashboard samples (not only a synthetic flat baseline)
    const hasRealHistory = history.length >= 2;
    return {
      viewsDay: viewsDayAbs,
      starsDay: starsDayAbs,
      viewsDayAbs,
      starsDayAbs,
      viewsDayPct: percentChange(prev.views, curr.views),
      starsDayPct: percentChange(prev.stars, curr.stars),
      viewsPeriodPct: hasRealHistory ? percentChange(first.views, curr.views) : null,
      starsPeriodPct: hasRealHistory ? percentChange(first.stars, curr.stars) : null,
      periodDays: filled.length,
      hasRealHistory,
    };
  }, [filled, history.length]);

  if (products.length === 0) return null;

  const viewsDayLabel = formatDelta(growth.viewsDayAbs);
  const starsDayLabel = formatDelta(growth.starsDayAbs);
  const viewsDayPctLabel = formatPercent(growth.viewsDayPct);
  const starsDayPctLabel = formatPercent(growth.starsDayPct);
  const viewsPeriodPctLabel = formatPercent(growth.viewsPeriodPct);
  const starsPeriodPctLabel = formatPercent(growth.starsPeriodPct);

  const rootStyle = {
    '--insights-accent': accentColor,
  } as CSSProperties;

  const showBaselineHint = history.length < 2;

  return (
    <section className={classes.root} style={rootStyle} aria-label={t('dashboard.insights.title')}>
      <PageHeader
        variant="section"
        title={t('dashboard.insights.title')}
        subtitle={expanded ? t('dashboard.insights.subtitle') : undefined}
        accentColor={accentColor}
        mb={expanded ? 14 : 10}
        mt={10}
        actions={
          <Tooltip
            label={
              expanded
                ? t('dashboard.insights.collapse')
                : t('dashboard.insights.expand')
            }
            withArrow
            position="left"
          >
            <UnstyledButton
              type="button"
              className={`${classes.toggleBtn} ${expanded ? classes.toggleBtnOpen : ''}`}
              onClick={toggleExpanded}
              aria-expanded={expanded}
              aria-controls="dashboard-insights-panel"
              aria-label={
                expanded
                  ? t('dashboard.insights.collapse')
                  : t('dashboard.insights.expand')
              }
            >
              <IconChevronDown size={18} stroke={2} />
            </UnstyledButton>
          </Tooltip>
        }
      />

      <Collapse in={!expanded} transitionDuration={220}>
        <UnstyledButton
          type="button"
          className={classes.miniPanel}
          onClick={toggleExpanded}
          aria-label={t('dashboard.insights.expand')}
        >
          <div className={classes.miniRow}>
            <span className={classes.miniChip}>
              <IconPackage size={13} stroke={1.75} aria-hidden />
              <strong>{stats.totalProducts}</strong>
              <span>{t('dashboard.insights.totalProducts')}</span>
            </span>
            <span className={classes.miniChip}>
              <IconEye size={13} stroke={1.75} aria-hidden />
              <strong>{stats.totalViews}</strong>
              <span>{t('dashboard.insights.totalViews')}</span>
            </span>
            <span className={classes.miniChip}>
              <IconStar size={13} stroke={1.75} aria-hidden />
              <strong>{stats.totalStars}</strong>
              <span>{t('dashboard.insights.totalStars')}</span>
            </span>
            <span className={classes.miniChip}>
              <IconWorld size={13} stroke={1.75} aria-hidden />
              <strong>{stats.publicCount}</strong>
              <span>{t('dashboard.insights.publicProducts')}</span>
            </span>
            <span className={classes.miniChip}>
              <IconListCheck size={13} stroke={1.75} aria-hidden />
              <strong>{stats.listedCount}</strong>
              <span>{t('dashboard.insights.listedProducts')}</span>
            </span>
          </div>

          <div className={classes.miniRow}>
            {viewsDayLabel || viewsDayPctLabel ? (
              <span
                className={`${classes.miniTrend} ${
                  (growth.viewsDayAbs ?? 0) >= 0 && (growth.viewsDayPct ?? 0) >= 0
                    ? classes.miniTrendUp
                    : classes.miniTrendDown
                }`}
              >
                <IconTrendingUp size={13} stroke={1.75} aria-hidden />
                <span className={classes.miniTrendLabel}>
                  {t('dashboard.insights.viewsTrend')}
                </span>
                {viewsDayLabel ? <strong>{viewsDayLabel}</strong> : null}
                {viewsDayPctLabel ? (
                  <span className={classes.miniTrendPct}>{viewsDayPctLabel}</span>
                ) : null}
                <span className={classes.miniTrendMeta}>{t('dashboard.insights.miniToday')}</span>
              </span>
            ) : null}

            {starsDayLabel || starsDayPctLabel ? (
              <span
                className={`${classes.miniTrend} ${
                  (growth.starsDayAbs ?? 0) >= 0 && (growth.starsDayPct ?? 0) >= 0
                    ? classes.miniTrendUp
                    : classes.miniTrendDown
                }`}
              >
                <IconTrendingUp size={13} stroke={1.75} aria-hidden />
                <span className={classes.miniTrendLabel}>
                  {t('dashboard.insights.starsTrend')}
                </span>
                {starsDayLabel ? <strong>{starsDayLabel}</strong> : null}
                {starsDayPctLabel ? (
                  <span className={classes.miniTrendPct}>{starsDayPctLabel}</span>
                ) : null}
                <span className={classes.miniTrendMeta}>{t('dashboard.insights.miniToday')}</span>
              </span>
            ) : null}

            {viewsPeriodPctLabel ? (
              <span
                className={`${classes.miniTrend} ${
                  (growth.viewsPeriodPct ?? 0) >= 0 ? classes.miniTrendUp : classes.miniTrendDown
                }`}
              >
                <IconEye size={13} stroke={1.75} aria-hidden />
                <span className={classes.miniTrendLabel}>
                  {t('dashboard.insights.totalViews')}
                </span>
                <strong>{viewsPeriodPctLabel}</strong>
                <span className={classes.miniTrendMeta}>
                  {t('dashboard.insights.miniPeriod', { days: growth.periodDays })}
                </span>
              </span>
            ) : null}

            {starsPeriodPctLabel ? (
              <span
                className={`${classes.miniTrend} ${
                  (growth.starsPeriodPct ?? 0) >= 0 ? classes.miniTrendUp : classes.miniTrendDown
                }`}
              >
                <IconStar size={13} stroke={1.75} aria-hidden />
                <span className={classes.miniTrendLabel}>
                  {t('dashboard.insights.totalStars')}
                </span>
                <strong>{starsPeriodPctLabel}</strong>
                <span className={classes.miniTrendMeta}>
                  {t('dashboard.insights.miniPeriod', { days: growth.periodDays })}
                </span>
              </span>
            ) : null}

            {!viewsDayLabel &&
            !starsDayLabel &&
            !viewsPeriodPctLabel &&
            !starsPeriodPctLabel ? (
              <span className={classes.miniTrendHint}>
                <IconTrendingUp size={13} stroke={1.75} aria-hidden />
                {t('dashboard.insights.miniNoTrend')}
              </span>
            ) : null}

            <span className={classes.miniExpandHint}>
              {t('dashboard.insights.expand')}
              <IconChevronDown size={14} stroke={2} aria-hidden />
            </span>
          </div>
        </UnstyledButton>
      </Collapse>

      <Collapse in={expanded} transitionDuration={280}>
        <div className={classes.expandedBody} id="dashboard-insights-panel">
      <div className={classes.statGrid}>
        <div className={classes.statCard}>
          <span className={classes.statIcon} aria-hidden>
            <IconPackage size={18} stroke={1.75} />
          </span>
          <span className={classes.statMeta}>
            <span className={classes.statValue}>{stats.totalProducts}</span>
            <span className={classes.statLabel}>{t('dashboard.insights.totalProducts')}</span>
          </span>
        </div>

        <div className={classes.statCard}>
          <span className={classes.statIcon} aria-hidden>
            <IconEye size={18} stroke={1.75} />
          </span>
          <span className={classes.statMeta}>
            <span className={classes.statValueRow}>
              <span className={classes.statValue}>{stats.totalViews}</span>
              {viewsDayLabel ? (
                <span
                  className={`${classes.delta} ${growth.viewsDayAbs! >= 0 ? classes.deltaUp : classes.deltaDown}`}
                >
                  {viewsDayLabel}
                </span>
              ) : null}
              {viewsDayPctLabel ? (
                <span
                  className={`${classes.deltaPct} ${growth.viewsDayPct! >= 0 ? classes.deltaUp : classes.deltaDown}`}
                >
                  {viewsDayPctLabel}
                </span>
              ) : null}
            </span>
            <span className={classes.statLabel}>{t('dashboard.insights.totalViews')}</span>
          </span>
        </div>

        <div className={classes.statCard}>
          <span className={classes.statIcon} aria-hidden>
            <IconStar size={18} stroke={1.75} />
          </span>
          <span className={classes.statMeta}>
            <span className={classes.statValueRow}>
              <span className={classes.statValue}>{stats.totalStars}</span>
              {starsDayLabel ? (
                <span
                  className={`${classes.delta} ${growth.starsDayAbs! >= 0 ? classes.deltaUp : classes.deltaDown}`}
                >
                  {starsDayLabel}
                </span>
              ) : null}
              {starsDayPctLabel ? (
                <span
                  className={`${classes.deltaPct} ${growth.starsDayPct! >= 0 ? classes.deltaUp : classes.deltaDown}`}
                >
                  {starsDayPctLabel}
                </span>
              ) : null}
            </span>
            <span className={classes.statLabel}>{t('dashboard.insights.totalStars')}</span>
          </span>
        </div>

        <div className={classes.statCard}>
          <span className={classes.statIcon} aria-hidden>
            <IconWorld size={18} stroke={1.75} />
          </span>
          <span className={classes.statMeta}>
            <span className={classes.statValue}>{stats.publicCount}</span>
            <span className={classes.statLabel}>{t('dashboard.insights.publicProducts')}</span>
          </span>
        </div>

        <div className={classes.statCard}>
          <span className={classes.statIcon} aria-hidden>
            <IconListCheck size={18} stroke={1.75} />
          </span>
          <span className={classes.statMeta}>
            <span className={classes.statValue}>{stats.listedCount}</span>
            <span className={classes.statLabel}>{t('dashboard.insights.listedProducts')}</span>
          </span>
        </div>
      </div>

      <div className={classes.trendGrid}>
        <div className={classes.chartCard}>
          <div className={classes.chartHeaderRow}>
            <div className={classes.chartHeader}>
              <IconTrendingUp size={16} stroke={1.75} />
              <h3 className={classes.chartTitle}>{t('dashboard.insights.viewsTrend')}</h3>
            </div>
            {viewsPeriodPctLabel ? (
              <span
                className={`${classes.periodBadge} ${growth.viewsPeriodPct! >= 0 ? classes.deltaUp : classes.deltaDown}`}
              >
                {t('dashboard.insights.periodChange', {
                  pct: viewsPeriodPctLabel,
                  days: growth.periodDays,
                })}
              </span>
            ) : null}
          </div>
          <TrendLineChart
            series={filled}
            metric="views"
            accentColor={accentColor}
            locale={i18n.language}
            emptyLabel={t('dashboard.insights.noViewsYet')}
          />
          <p className={classes.chartSub}>{t('dashboard.insights.dailyGains')}</p>
          <DailyGainBars
            series={filled}
            metric="views"
            accentColor={accentColor}
            locale={i18n.language}
            emptyLabel={t('dashboard.insights.noViewsYet')}
          />
        </div>

        <div className={classes.chartCard}>
          <div className={classes.chartHeaderRow}>
            <div className={classes.chartHeader}>
              <IconTrendingUp size={16} stroke={1.75} />
              <h3 className={classes.chartTitle}>{t('dashboard.insights.starsTrend')}</h3>
            </div>
            {starsPeriodPctLabel ? (
              <span
                className={`${classes.periodBadge} ${growth.starsPeriodPct! >= 0 ? classes.deltaUp : classes.deltaDown}`}
              >
                {t('dashboard.insights.periodChange', {
                  pct: starsPeriodPctLabel,
                  days: growth.periodDays,
                })}
              </span>
            ) : null}
          </div>
          <TrendLineChart
            series={filled}
            metric="stars"
            accentColor={accentColor}
            locale={i18n.language}
            emptyLabel={t('dashboard.insights.noStarsYet')}
          />
          <p className={classes.chartSub}>{t('dashboard.insights.dailyGains')}</p>
          <DailyGainBars
            series={filled}
            metric="stars"
            accentColor={accentColor}
            locale={i18n.language}
            emptyLabel={t('dashboard.insights.noStarsYet')}
          />
        </div>
      </div>

      <div className={classes.charts}>
        <ExpandableTopListCard
          title={t('dashboard.insights.topViews')}
          icon={<IconEye size={16} stroke={1.75} />}
          hint={t('dashboard.insights.topViewsHint')}
          items={stats.byViews}
          accentColor={accentColor}
          emptyLabel={t('dashboard.insights.noViewsYet')}
        />

        <ExpandableTopListCard
          title={t('dashboard.insights.topStars')}
          icon={<IconStar size={16} stroke={1.75} />}
          hint={t('dashboard.insights.topStarsHint')}
          items={stats.byStars}
          accentColor={accentColor}
          fillOpacity={0.85}
          emptyLabel={t('dashboard.insights.noStarsYet')}
        />

        <ExpandableTopListCard
          title={t('dashboard.insights.topEngagement')}
          icon={<IconHeartRateMonitor size={16} stroke={1.75} />}
          hint={t('dashboard.insights.topEngagementHint')}
          items={stats.byEngagement}
          accentColor={accentColor}
          emptyLabel={t('dashboard.insights.noEngagementYet')}
        />

        <ExpandableTopListCard
          title={t('dashboard.insights.needsAttention')}
          icon={<IconAlertTriangle size={16} stroke={1.75} />}
          hint={t('dashboard.insights.needsAttentionHint')}
          items={stats.needsAttention}
          accentColor={accentColor}
          fillOpacity={0.7}
          emptyLabel={t('dashboard.insights.noPublicProducts')}
        />

        <ExpandableTopListCard
          title={t('dashboard.insights.topListed')}
          icon={<IconApps size={16} stroke={1.75} />}
          hint={t('dashboard.insights.topListedHint')}
          items={stats.byListings}
          accentColor={accentColor}
          emptyLabel={t('dashboard.insights.noListingsYet')}
        />

        <ExpandableTopListCard
          title={t('dashboard.insights.privateWithViews')}
          icon={<IconWorld size={16} stroke={1.75} />}
          hint={t('dashboard.insights.privateWithViewsHint')}
          items={stats.privateWithViews}
          accentColor={accentColor}
          emptyLabel={t('dashboard.insights.noPrivateWithViews')}
        />
      </div>

      <p className={classes.footnote}>
        {showBaselineHint
          ? t('dashboard.insights.baselineHint')
          : t('dashboard.insights.trendHint')}
      </p>
        </div>
      </Collapse>
    </section>
  );
}
